import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '@environments/environment';
import { Review, ReviewResult } from '@app/shared/models/review.model';
import { FetchState, PatchState } from '@app/shared/models/fetch-state.model';
import { HttpService } from '@app/shared/services/http';

export interface ReviewDraft {
  reviewResult: ReviewResult;
  comment: string | null;
  reviewed: Date | string | null;
}

@Injectable({ providedIn: 'root' })
export class ReviewService {
  private readonly baseUrl = `${environment.apiUrl}Review`;

  private readonly reviewStateSubject = new BehaviorSubject<
    FetchState<Review[]>
  >({ status: 'loading' });
  public reviewState$ = this.reviewStateSubject.asObservable();

  private patchStateSubject = new BehaviorSubject<PatchState>({
    status: 'idle',
  });
  public patchState$ = this.patchStateSubject.asObservable();

  private reviewDrafts = new Map<string, ReviewDraft>();

  constructor(private readonly httpService: HttpService) {}

  isInitialLoad(): boolean {
    return this.reviewStateSubject.getValue().status === 'loading';
  }

  fetchReviews(hospitalCode?: string | null): void {
    this.reviewStateSubject.next({ status: 'loading' });
    const url = new URL(this.baseUrl);
    if (hospitalCode) {
      url.searchParams.set('hospitalcode', hospitalCode);
    }

    this.httpService.get<{ items: Review[] }>(url.toString()).subscribe({
      next: (response) =>
        this.reviewStateSubject.next({
          status: 'success',
          data: response.items,
        }),
      error: (error) =>
        this.reviewStateSubject.next({ status: 'error', error }),
    });
  }

  patchReview(id: string, patch: Partial<Review>): void {
    this.patchStateSubject.next({ status: 'saving' });
    this.httpService
      .patch<{ message: string }>(`${this.baseUrl}/${id}`, patch)
      .subscribe({
        next: (response) => {
          this.reviewDrafts.delete(id);
          this.patchStateSubject.next({ status: 'success', data: response });
        },
        error: (error) =>
          this.patchStateSubject.next({ status: 'error', error }),
      });
  }

  resetPatchState(): void {
    this.patchStateSubject.next({ status: 'idle' });
  }

  saveDraft(id: string, draft: ReviewDraft): void {
    this.reviewDrafts.set(id, {
      reviewResult: draft.reviewResult ?? null,
      comment: draft.comment ?? null,
      reviewed: draft.reviewed ?? null,
    });
  }

  getDraft(id: string): ReviewDraft | undefined {
    return this.reviewDrafts.get(id);
  }

  removeDraft(id: string): void {
    this.reviewDrafts.delete(id);
  }
}
